import { AcceptUserInviteMutation } from '@graphql/generated';

type AcceptUserInviteResult = AcceptUserInviteMutation['acceptUserInvite'];

/** Translation keys (models namespace) for the generic-invite toasts. */
export type GenericInviteToastKey =
  | 'genericInvite.alreadyFriends'
  | 'genericInvite.alreadyRegistered'
  | 'genericInvite.acceptFailed';

/** What the app does once acceptUserInvite has resolved (or failed). */
export type GenericInviteAction =
  | { kind: 'open-chat'; chatId: string }
  | { kind: 'toast'; messageKey: GenericInviteToastKey }
  // The request itself failed: keep the token so it is retried later.
  | { kind: 'retry'; messageKey: 'genericInvite.acceptFailed' };

/**
 * Maps an acceptUserInvite result to the follow-up for an existing user who
 * opened a generic Contacts invite (TA-11035, backend TA-11131):
 * - request_sent    → open the DM chat with the inviter, where the inviter's
 *                     friend request waits to be accepted
 * - already_friends → "You're already friends." (the backend also returns this
 *                     when it just approved the user's own pending request)
 * - self / invalid / unknown status → the existing "already on TaskTag" toast
 * - no result (network / GraphQL failure) → `retry`: say it will be retried;
 *                     callers keep the token. Every server answer above is
 *                     final, so callers drop the token for those.
 */
export const decideGenericInviteAction = (
  result: AcceptUserInviteResult | null | undefined
): GenericInviteAction => {
  if (!result) {
    return { kind: 'retry', messageKey: 'genericInvite.acceptFailed' };
  }
  const chatId = result.chat?.id;
  if (result.status === 'request_sent' && chatId) {
    return { kind: 'open-chat', chatId };
  }
  if (result.status === 'already_friends') {
    return { kind: 'toast', messageKey: 'genericInvite.alreadyFriends' };
  }
  return { kind: 'toast', messageKey: 'genericInvite.alreadyRegistered' };
};
